import { useState, type CSSProperties } from "react";

import { cn } from "~/lib/utils";

/**
 * Initials from a human identity string — an email or a display name. A name
 * takes the first letter of its first two words; an email takes its local
 * part, split on the common separators. Never a claimed name, just a glyph
 * handle (same law as the Inbox chips).
 */
export function avatarInitials(identity: string | null | undefined): string {
  const trimmed = identity?.trim() ?? "";
  if (trimmed.length === 0) return "?";
  const base = trimmed.includes("@") ? (trimmed.split("@")[0] ?? trimmed) : trimmed;
  const parts = base.split(/[\s._-]+/).filter((part) => part.length > 0);
  if (parts.length >= 2) {
    return `${parts[0]!.charAt(0)}${parts[1]!.charAt(0)}`.toUpperCase();
  }
  return base.slice(0, 2).toUpperCase();
}

/**
 * The one avatar primitive: an image when a source exists and loads, the
 * identity's initials otherwise. The `src` seat carries the Actor profile's
 * signed photo URL (US-CAN-08, `ActorProfile.avatarUrl`); a broken or
 * expired image falls back to the initials rather than a blank.
 */
export function Avatar({
  className,
  label,
  presence,
  src,
}: {
  readonly className?: string;
  /** The identity the initials derive from; also the accessible name. */
  readonly label: string | null;
  /**
   * "online" wears the presence ring (the `--ncl-positive` theme token, the
   * same color as the Inbox presence dot). Omit when presence is unknown —
   * an Actor may be offline, hidden by their own visibility gate, or simply
   * unread, and the face must not claim to tell those apart.
   */
  readonly presence?: "online";
  readonly src?: string | null;
}) {
  const [broken, setBroken] = useState(false);
  const showImage = typeof src === "string" && src.length > 0 && !broken;
  return (
    <span
      aria-hidden={label === null ? true : undefined}
      className={cn(
        "flex size-6 shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-accent/40 text-[9px] font-medium text-muted-foreground",
        presence === "online" && "ring-2",
        className,
      )}
      data-presence={presence}
      data-slot="avatar"
      role="img"
      aria-label={label ?? undefined}
      style={
        presence === "online"
          ? ({ "--tw-ring-color": "var(--ncl-positive, #3fa870)" } as CSSProperties)
          : undefined
      }
    >
      {showImage ? (
        <img
          alt=""
          className="size-full object-cover"
          draggable={false}
          onError={() => setBroken(true)}
          src={src}
        />
      ) : (
        avatarInitials(label)
      )}
    </span>
  );
}
